'use client';

import {forwardRef, MouseEvent, ReactNode} from 'react';
import Link from 'next/link';
import {Button, ButtonProps} from '@/shadcn/components/ui/button';

/**
 * AffiliateButton — renders a <button> for /go/ affiliate URLs so Googlebot
 * does not discover them via href crawling. On click, opens the tracking URL
 * in a new tab. The /go/ route itself (signature, tracking, GA4, redirect) is
 * unchanged — only the frontend trigger differs.
 *
 * Werbekennzeichnung (UWG §5a): die Pflicht-Kennzeichnung erfolgt sammelhaft
 * pro Page über <AffiliateDisclosure/> am Page-Ende, nicht inline pro Button.
 * Sonst rendern wir auf einer Produkt-Detail mit n Shops n Sterndchen, was den
 * UI laut macht und die Marke nervig wirken lässt. Branchen-Standard
 * (idealo/billiger.de): eine Sammelkennzeichnung pro Page reicht im klar
 * erkennbaren Preisvergleich-Kontext.
 *
 * Fallback: if trackingUrl is empty and fallbackHref is provided, renders a
 * regular <Link> to the internal anchor (e.g. "#preisvergleich"). This keeps
 * internal navigation as <a href> for SEO.
 */
export interface AffiliateButtonProps extends Omit<ButtonProps, 'onClick'> {
    trackingUrl: string | null | undefined;
    fallbackHref?: string;
    children: ReactNode;
    /**
     * Tracking metadata — set these so OutboundClickTracker can build a
     * correct GA4 generate_lead event. Without them, shop_name falls back
     * to 'unknown' and items[] is undefined.
     */
    shopName?: string;
    variantId?: string;
    variantTitle?: string;
    variantPrice?: number;
    itemCategory?: string;
    itemBrand?: string;
}

export const AffiliateButton = forwardRef<HTMLButtonElement, AffiliateButtonProps>(
    function AffiliateButton(
        {trackingUrl, fallbackHref, children, className, variant, size, shopName, variantId, variantTitle, variantPrice, itemCategory, itemBrand, ...rest},
        ref,
    ) {
        if (trackingUrl) {
            const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
                // Allow the global OutboundClickTracker to fire GA4 event first
                // (it listens in capture phase). Then open the redirect URL.
                // noopener,noreferrer mirrors the previous rel="noopener noreferrer".
                window.open(trackingUrl, '_blank', 'noopener,noreferrer');
                e.preventDefault();
            };

            return (
                <Button
                    ref={ref}
                    type="button"
                    variant={variant}
                    size={size}
                    className={className}
                    onClick={handleClick}
                    data-affiliate-url={trackingUrl}
                    data-shop-name={shopName}
                    data-variant-id={variantId}
                    data-variant-title={variantTitle}
                    data-variant-price={variantPrice}
                    data-item-category={itemCategory}
                    data-item-brand={itemBrand}
                    {...rest}
                >
                    {children}
                </Button>
            );
        }

        if (fallbackHref) {
            return (
                <Link
                    href={fallbackHref}
                    prefetch={false}
                    className={className}
                >
                    <Button
                        type="button"
                        variant={variant}
                        size={size}
                        {...rest}
                    >
                        {children}
                    </Button>
                </Link>
            );
        }

        return (
            <Button
                ref={ref}
                type="button"
                variant={variant}
                size={size}
                className={className}
                disabled
                {...rest}
            >
                {children}
            </Button>
        );
    },
);
